import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeTable } from "./useRealtime";
import type { Email } from "@/lib/types";

const EMAIL_REALTIME_KEYS = ["emails", "inbox-stats"];

export function useEmails(gmailAccounts?: string[], excludeNoise?: boolean) {
  const { user } = useAuth();
  useRealtimeTable("emails", EMAIL_REALTIME_KEYS);
  const accountKey = (gmailAccounts || []).slice().sort().join(",");
  return useQuery({
    queryKey: ["emails", user?.id, accountKey, excludeNoise],
    queryFn: async () => {
      if (!user) return [];
      let query = supabase
        .from("emails")
        .select("*")
        .eq("user_id", user.id)
        .order("date", { ascending: false })
        .limit(100);

      if (gmailAccounts && gmailAccounts.length === 1) {
        query = query.eq("gmail_account", gmailAccounts[0]);
      } else if (gmailAccounts && gmailAccounts.length > 1) {
        query = query.in("gmail_account", gmailAccounts);
      }

      if (excludeNoise) {
        // "Cut the BS" = only show important (real humans needing a reply) and unclassified
        query = query.or("ai_category.is.null,ai_category.eq.important");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Email[];
    },
    enabled: !!user,
  });
}

export function useClassifyEmails() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (force?: boolean) => {
      const { data, error } = await supabase.functions.invoke("classify-emails", {
        body: force ? { force: true } : {},
      });
      if (error) throw error;
      return data as { classified: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emails"] });
    },
  });
}

export function useEmail(id: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["email", id],
    queryFn: async () => {
      if (!user || !id) return null;
      const { data, error } = await supabase
        .from("emails")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Email;
    },
    enabled: !!user && !!id,
  });
}

export function useGmailAccounts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["gmail-accounts", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("integrations")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "gmail")
        .eq("enabled", true);
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
}

export function useGmailConnect() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("gmail-auth", {
        body: {},
      });
      if (error) throw error;
      return data as { url: string };
    },
  });
}

export function useGmailSend() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      integration_id: string;
      to: string;
      subject: string;
      body: string;
      in_reply_to?: string;
      thread_id?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("gmail-send", {
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emails"] });
    },
  });
}

export function useMarkEmailRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from("emails")
        .update({ is_read: true })
        .eq("id", emailId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emails"] });
      queryClient.invalidateQueries({ queryKey: ["inbox-stats"] });
    },
  });
}

export function useGmailDraft() {
  return useMutation({
    mutationFn: async (emailId: string) => {
      const { data, error } = await supabase.functions.invoke("gmail-draft", {
        body: { email_id: emailId },
      });
      if (error) throw error;
      return data as { draft: string };
    },
  });
}
